import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const PracticeManagement = () => {
  const [activeFeature, setActiveFeature] = useState(null);

  const features = [
    {
      id: 'scheduling',
      title: 'AI Smart Scheduling',
      description: "Intelligent appointment scheduling that optimizes your practice's workflow",
      icon: '📅',
      link: '/smart-scheduling'
    },
    {
      id: 'analytics',
      title: 'Predictive Analytics',
      description: 'AI-powered insights for better business decisions and growth',
      icon: '📊',
      link: '/analytics'
    },
    {
      id: 'communication',
      title: 'Intelligent Patient Communication',
      description: 'Automated, personalized patient interactions and follow-ups',
      icon: '💬',
      link: '/patient-communication'
    }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E5F0F3] to-white">
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#1E2A4A] mb-6">
              AI-Powered Practice Management
            </h1>
            <p className="text-xl text-gray-600">
              Transform your dental practice operations with our AI-driven comprehensive management solution
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h2 className="text-2xl font-bold text-[#1E2A4A] mb-4">Key Features</h2>
              <ul className="space-y-4">
                {features.map((feature) => (
                  <li 
                    key={feature.id}
                    className={`flex items-start space-x-3 p-4 rounded-xl cursor-pointer transition-all ${
                      activeFeature === feature.id ? 'bg-orange-50' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setActiveFeature(feature.id)}
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-2xl">
                      {feature.icon}
                    </div>
                    <div className="flex-grow">
                      <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                      <Link 
                        to={feature.link}
                        className="inline-block mt-2 text-orange-500 hover:text-orange-600 font-medium"
                      >
                        Read More →
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#001233] text-white rounded-2xl p-8 shadow-xl">
              <h2 className="text-2xl font-bold mb-4">Benefits</h2>
              <ul className="space-y-4">
                {[
                  { value: '40%', label: 'Reduction in administrative tasks' },
                  { value: '95%', label: 'Scheduling accuracy' },
                  { value: '24/7', label: 'Automated support' }
                ].map((benefit, index) => (
                  <li 
                    key={index}
                    className="flex items-center space-x-3 hover:scale-105 transition-transform"
                  >
                    <div className="text-3xl font-bold text-orange-500">{benefit.value}</div>
                    <div className="text-gray-300">{benefit.label}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right Content */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h2 className="text-2xl font-bold text-[#1E2A4A] mb-6">How It Works</h2>
              <div className="space-y-6">
                {[
                  {
                    step: 1,
                    title: 'Data Integration',
                    description: "AI analyzes your practice's historical data and patterns"
                  },
                  {
                    step: 2,
                    title: 'Smart Optimization',
                    description: 'AI optimizes scheduling, staffing, and resource allocation'
                  },
                  {
                    step: 3,
                    title: 'Automated Management',
                    description: 'AI handles routine tasks and provides actionable insights'
                  }
                ].map((step) => (
                  <div 
                    key={step.step}
                    className="flex items-start space-x-4 hover:scale-102 transition-transform"
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">
                      {step.step}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{step.title}</h3>
                      <p className="text-gray-600">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-8 shadow-xl text-white">
              <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
              <p className="mb-6">Transform your practice management with AI</p>
              <Link 
                to="/schedule-demo"
                className="block w-full bg-white text-orange-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors text-center"
              >
                Schedule a Demo
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticeManagement; 